import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { Layers, Plus, Upload, FileText, Edit, X } from 'lucide-react';
import './AdminPages.css';


const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showProjModal, setShowProjModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedProj, setSelectedProj] = useState(null);

  const [projForm, setProjForm] = useState({ name: '', short_description: '', project_workflow: '', is_active: true });
  const [docForm, setDocForm] = useState({ document_name: '', description: '', file: null });

  const { addToast } = useToast();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await api.get('/projects/');
      setProjects(res.data.results || res.data);
    } catch (err) {
      console.error(err);
      addToast('Failed to load projects.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProject = async (e) => {
    e.preventDefault();
    try {
      if (selectedProj && showProjModal) {
        await api.put(`/projects/${selectedProj.id}/`, projForm);
        addToast('Project updated successfully!', 'success');
      } else {
        await api.post('/projects/', projForm);
        addToast('Project created successfully!', 'success');
      }
      setShowProjModal(false);
      setSelectedProj(null);
      fetchProjects();
    } catch (err) {
      console.error(err);
      addToast('Failed to save project.', 'error');
    }
  };

  const handleUploadDoc = async (e) => {
    e.preventDefault();
    if (!docForm.file) {
      addToast('Please select a file to upload.', 'error');
      return;
    }

    const payload = new FormData();
    payload.append('project', selectedProj.id);
    payload.append('document_name', docForm.document_name);
    payload.append('description', docForm.description);
    payload.append('document', docForm.file);

    try {
      await api.post('/project-documents/', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      addToast('Project document uploaded and indexed into AI RAG!', 'success');
      setShowDocModal(false);
      setDocForm({ document_name: '', description: '', file: null });
      fetchProjects();
    } catch (err) {
      console.error(err);
      addToast('Failed to upload document.', 'error');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <div style={{ display: 'flex', flex: 1 }}>
        <Sidebar />
        <main style={{ flex: 1, padding: '32px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
            <div>
              <h1 style={{ fontSize: '1.8rem', fontWeight: 800 }}>Project Portfolio & AI Docs</h1>
              <p style={{ color: '#64748b' }}>Create projects, write rich technical workflows, and upload documentation for AI RAG index.</p>
            </div>
            <button className="btn btn-primary" onClick={() => { setSelectedProj(null); setProjForm({ name: '', short_description: '', project_workflow: '', is_active: true }); setShowProjModal(true); }}>
              <Plus size={18} /> Create Project
            </button>
          </div>

          {loading ? <LoadingSpinner label="Loading project portfolio..." /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {projects.map((proj) => (
                <div key={proj.id} className="card" style={{ padding: '28px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#f8fafc' }}>{proj.name}</h2>
                        <span className={`badge ${proj.is_active ? 'badge-success' : 'badge-warning'}`}>
                          {proj.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '4px' }}>{proj.short_description}</p>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedProj(proj); setShowDocModal(true); }}>
                        <Upload size={14} /> Upload Doc
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedProj(proj); setProjForm({ name: proj.name, short_description: proj.short_description, project_workflow: proj.project_workflow, is_active: proj.is_active }); setShowProjModal(true); }}>
                        <Edit size={14} /> Edit Project
                      </button>
                    </div>
                  </div>

                  {/* Documents List */}
                  <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '16px', marginTop: '16px' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#cbd5e1', marginBottom: '10px' }}>
                      Indexed Knowledge Documents ({proj.documents ? proj.documents.length : 0})
                    </div>
                    {proj.documents && proj.documents.length > 0 ? (
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {proj.documents.map(d => (
                          <div key={d.id} style={{ padding: '8px 12px', background: 'rgba(255, 255, 255, 0.07)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.15)', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FileText size={14} style={{ color: '#38bdf8' }} />
                            <span style={{ color: '#f1f5f9', fontWeight: 500 }}>{d.document_name}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>No documentation files attached yet.</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Modal: Create/Edit Project */}
          {showProjModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '32px', maxWidth: '700px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{selectedProj ? 'Edit Project' : 'Create Project'}</h2>
                  <button onClick={() => setShowProjModal(false)} style={{ background: 'transparent', color: '#94a3b8', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
                </div>
                <form onSubmit={handleSaveProject}>
                  <div className="form-group">
                    <label className="form-label">Project Name *</label>
                    <input type="text" className="form-input" required value={projForm.name} onChange={e => setProjForm({...projForm, name: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Short Description *</label>
                    <input type="text" className="form-input" required value={projForm.short_description} onChange={e => setProjForm({...projForm, short_description: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Project Workflow (HTML / Rich Text) *</label>
                    <textarea className="form-textarea" rows={8} required placeholder="<h3>Overview</h3><p>Tech stack and Git workflow...</p>" value={projForm.project_workflow} onChange={e => setProjForm({...projForm, project_workflow: e.target.value})} />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowProjModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Save Project</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal: Upload Document */}
          {showDocModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Upload Knowledge Document to {selectedProj?.name}</h2>
                  <button onClick={() => setShowDocModal(false)} style={{ background: 'transparent', color: '#94a3b8', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
                </div>
                <form onSubmit={handleUploadDoc}>
                  <div className="form-group">
                    <label className="form-label">Document Display Name *</label>
                    <input type="text" className="form-input" required placeholder="e.g. SalesRoute API Specs.pdf" value={docForm.document_name} onChange={e => setDocForm({...docForm, document_name: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <input type="text" className="form-input" placeholder="Brief summary of document content" value={docForm.description} onChange={e => setDocForm({...docForm, description: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Select File (PDF, DOCX, TXT) *</label>
                    <input type="file" className="form-input" required accept=".pdf,.docx,.txt" onChange={e => setDocForm({...docForm, file: e.target.files[0]})} />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowDocModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Upload & Index into RAG</button>
                  </div>
                </form>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminProjects;
